import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import {useCallback, useState, useTransition, useMemo, useEffect, memo} from "react";
import {SECTIONS} from "../helpers";
import {CONSTANTS} from "../../../constants/constants";
import {SelectBtn} from "../../common/btns/SelectBtn/SelectBtn";
import {RadioGroup} from "../../selectors/groups/RadioGroup/RadioGroup";
import {setCurrencyThunk, setLanguageThunk} from "../../../redux/thunks/userThunk";
import s from './HeaderSelector.module.css';

export const HeaderSelector = memo(() => {

  const {S_LANGUAGES, S_CURRENCY} = SECTIONS;
  const {NAMESPACES, CONFIG} = CONSTANTS;

  const availableLanguages = useMemo(() => Object.values(CONFIG.LANGUAGES.AVAILABLE), [CONFIG]);
  const availableCurrencies = useMemo(() => Object.values(CONFIG.CURRENCY.AVAILABLE), [CONFIG]);

  const dispatch = useDispatch();
  const {language, currency} = useSelector(state => state.user);

  const {t} = useTranslation(NAMESPACES.COMMON);

  const [, startTransition] = useTransition();

  const [activeSection, setActiveSection] = useState(S_LANGUAGES);
  const [tempLanguage, setTempLanguage] = useState(language);
  const [tempCurrency, setTempCurrency] = useState(currency);

  useEffect(() => {
    setTempLanguage((prev) => prev?.TYPE === language?.TYPE ? prev : language);
  }, [language, setTempLanguage]);

  useEffect(() => {
    setTempCurrency((prev) => prev?.TYPE === currency?.TYPE ? prev : currency);
  }, [currency, setTempCurrency]);

  const handleLanguageChange = useCallback((selectedLanguage) => {
    if (selectedLanguage?.TYPE !== tempLanguage?.TYPE) {
      setTempLanguage(selectedLanguage);

      startTransition(() => {
        dispatch(setLanguageThunk({language: selectedLanguage}));
      });
    }
  }, [dispatch, tempLanguage, startTransition, setTempLanguage]);

  const handleCurrencyChange = useCallback((selectedCurrency) => {
    if (selectedCurrency?.TYPE !== tempCurrency?.TYPE) {
      setTempCurrency(selectedCurrency);
      startTransition(() => {
        dispatch(setCurrencyThunk({currency: selectedCurrency}));
      });
    }
  }, [dispatch, tempCurrency, startTransition, setTempCurrency]);

  const languageListContent = useMemo(() => (
    availableLanguages.map((item) => (
      <SelectBtn
        key={item?.TYPE}
        name={`${item?.NATIVE_NAME} (${item?.TYPE})`}
        isActive={item?.TYPE === tempLanguage?.TYPE}
        callback={() => handleLanguageChange(item)}
      />
    ))
  ), [availableLanguages, handleLanguageChange, tempLanguage]);

  const currencyListContent = useMemo(() => (
    availableCurrencies.map((item) => (
      <SelectBtn
        key={item?.TYPE}
        name={`${item?.TYPE} (${item?.SYMBOL})`}
        isActive={item?.TYPE === tempCurrency?.TYPE}
        callback={() => handleCurrencyChange(item)}
      />
    ))
  ), [availableCurrencies, handleCurrencyChange, tempCurrency]);

  const sectionsMap = {
    [S_LANGUAGES]: languageListContent,
    [S_CURRENCY]: currencyListContent,
  };

  const radioGroupContextConfig = useMemo(() => ([
    {value: S_LANGUAGES, title: t('language')},
    {value: S_CURRENCY, title: t('currency')},
  ]), [t, S_LANGUAGES, S_CURRENCY]);

  return (
    <div className={s.container}>
      <div className={s['buttons-block']}>
        <RadioGroup
          nameGroup={'language_currency'}
          items={radioGroupContextConfig}
          activeValue={activeSection}
          onChange={setActiveSection}
        />
      </div>
      <div className={'list-group-select-btn'}>
        {sectionsMap[activeSection] ?? languageListContent}
      </div>
    </div>
  );
})